import type { AnalysisInfoT, ParticipantT } from 'src/types/meeting';

const CACHE_KEY_PREFIX = 'teams-session-cache';
const CACHE_VERSION = 2;

const ALERTS_DISMISSED_PREFIX = 'teams-alerts-dismissed';
const ALERTS_CLOSED_PREFIX = 'teams-alerts-closed';

interface TeamsSessionCache {
  version: number;
  timestamp: number;
  botId: string;
  participants: Record<string, ParticipantT>;
  analysisInfo: AnalysisInfoT;
}

/** Cache key scoped by Teams meetingId — guarantees the cache belongs to the current meeting. */
export const getCacheKey = (meetingId: string) =>
  `${CACHE_KEY_PREFIX}:${meetingId}`;

/**
 * Returns the cached botId for a given meetingId, or null if no cache exists.
 * Used by CachedSessionGate to fast-track navigation after SDK resolves meetingId.
 */
export const getCachedBotId = (meetingId: string): string | null => {
  try {
    const raw = localStorage.getItem(getCacheKey(meetingId));
    if (!raw) return null;

    const parsed: TeamsSessionCache = JSON.parse(raw);
    if (parsed.version !== CACHE_VERSION) return null;

    return parsed.botId;
  } catch {
    return null;
  }
};

export const readSessionCache = (
  cacheKey: string,
): { participants: Record<string, ParticipantT>; analysisInfo: AnalysisInfoT } | null => {
  // A read signals a fresh mount — allow writes for this key again.
  suppressedKeys.delete(cacheKey);

  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed: TeamsSessionCache = JSON.parse(raw);
    if (parsed.version !== CACHE_VERSION) return null;

    return {
      participants: parsed.participants,
      analysisInfo: parsed.analysisInfo,
    };
  } catch {
    return null;
  }
};

let syncTimeout: ReturnType<typeof setTimeout> | null = null;
// Keys that have been cleared — writes to these keys are suppressed
// until the next readSessionCache call (which signals a fresh mount).
const suppressedKeys = new Set<string>();

export const writeSessionCache = (
  cacheKey: string,
  botId: string,
  participants: Record<string, ParticipantT>,
  analysisInfo: AnalysisInfoT,
) => {
  if (suppressedKeys.has(cacheKey)) return;

  if (syncTimeout) clearTimeout(syncTimeout);

  syncTimeout = setTimeout(() => {
    if (suppressedKeys.has(cacheKey)) return;

    try {
      const data: TeamsSessionCache = {
        version: CACHE_VERSION,
        timestamp: Date.now(),
        botId,
        participants,
        analysisInfo,
      };

      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch {
      // QuotaExceededError or other — silently skip
    }
  }, 300);
};

export const clearSessionCacheByMeetingId = (meetingId: string) => {
  const cacheKey = getCacheKey(meetingId);

  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }

  // Suppress any future writes from the still-mounted useEffect
  suppressedKeys.add(cacheKey);

  localStorage.removeItem(cacheKey);
};

const SESSION_PREFIXES = [
  CACHE_KEY_PREFIX,
  ALERTS_DISMISSED_PREFIX,
  ALERTS_CLOSED_PREFIX,
];

export const clearAllSessionCaches = () => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && SESSION_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  }
};

// --- Alert state persistence (scoped by botId) ---

export const getAlertsDismissedKey = (botId: string) =>
  `${ALERTS_DISMISSED_PREFIX}:${botId}`;

export const getAlertsClosedKey = (botId: string) =>
  `${ALERTS_CLOSED_PREFIX}:${botId}`;

export const readDismissedAlertKeys = (botId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(getAlertsDismissedKey(botId));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
};

export const writeDismissedAlertKeys = (
  botId: string,
  keys: Set<string>,
) => {
  try {
    localStorage.setItem(
      getAlertsDismissedKey(botId),
      JSON.stringify([...keys]),
    );
  } catch {
    // silently skip
  }
};

export const readUserClosedToast = (botId: string): boolean => {
  try {
    return localStorage.getItem(getAlertsClosedKey(botId)) === 'true';
  } catch {
    return false;
  }
};

export const writeUserClosedToast = (botId: string, closed: boolean) => {
  try {
    if (closed) {
      localStorage.setItem(getAlertsClosedKey(botId), 'true');
    } else {
      localStorage.removeItem(getAlertsClosedKey(botId));
    }
  } catch {
    // silently skip
  }
};

export const clearAlertsCacheByBotId = (botId: string) => {
  localStorage.removeItem(getAlertsDismissedKey(botId));
  localStorage.removeItem(getAlertsClosedKey(botId));
};
